import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ShortTextIcon from '@mui/icons-material/ShortText';
import ArticleIcon from '@mui/icons-material/Article';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TranslateIcon from '@mui/icons-material/Translate';
import { toast } from 'react-toastify';
import {
  EXERCISE_TYPES,
  getLesson,
  getExerciseType,
  resolveTargetWpm,
} from './typingTutorCurriculum';
import {
  fetchTypingProgress,
  saveTypingSession,
  fetchCourseStory,
  translateStoryText,
} from './typingTutorApi';
import { requireStudentAuth, parseStudentData, sanitizeStoriesTypingText } from './typingTutorUtils';
import TypingEngine from './TypingEngine';
import FingeringEngine from './FingeringEngine';
import './typingTutorTheme.css';

const ICON_MAP = {
  keyboard: KeyboardIcon,
  text_fields: TextFieldsIcon,
  short_text: ShortTextIcon,
  article: ArticleIcon,
  menu_book: MenuBookIcon,
};

const isLmsStoryId = (id) => String(id || '').startsWith('lms-');

export default function TypingTutorLesson() {
  const navigate = useNavigate();
  const { exerciseType, lessonId } = useParams();
  const [targetWpm, setTargetWpm] = useState(35);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingStory, setLoadingStory] = useState(isLmsStoryId(lessonId));
  const [lmsStory, setLmsStory] = useState(null);
  const [practiceText, setPracticeText] = useState('');
  const [translated, setTranslated] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [readyToType, setReadyToType] = useState(false);
  const lastSavedAttemptRef = useRef('');

  const exercise = getExerciseType(exerciseType);
  const staticLesson = getLesson(exerciseType, lessonId);
  const lesson = isLmsStoryId(lessonId) ? lmsStory : staticLesson;
  const student = parseStudentData();
  const Icon = ICON_MAP[exercise?.icon] || KeyboardIcon;

  const needsTranslation = Boolean(
    isLmsStoryId(lessonId) && lmsStory?.isTelugu && !translated,
  );

  useEffect(() => {
    if (!requireStudentAuth(navigate)) return undefined;
    const fallback = resolveTargetWpm(student?.enrolled_courses);
    setTargetWpm(fallback);
    fetchTypingProgress()
      .then((p) => {
        if (p?.targetWpm) setTargetWpm(p.targetWpm);
      })
      .catch(() => {});
  }, [navigate, student?.enrolled_courses]);

  useEffect(() => {
    if (!isLmsStoryId(lessonId)) {
      setLoadingStory(false);
      setReadyToType(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoadingStory(true);
        const story = await fetchCourseStory(lessonId);
        if (cancelled) return;
        setLmsStory(story);
        if (story.isTelugu) {
          setReadyToType(false);
          setPracticeText('');
        } else {
          setPracticeText(sanitizeStoriesTypingText(story.text));
          setReadyToType(true);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.message || 'Could not load story');
          navigate('/student/typing-tutor/stories');
        }
      } finally {
        if (!cancelled) setLoadingStory(false);
      }
    })();

    return () => { cancelled = true; };
  }, [lessonId, navigate]);

  const handleTranslate = async () => {
    if (!lmsStory?.originalText && !lmsStory?.text) return;
    setTranslating(true);
    try {
      const source = lmsStory.originalText || lmsStory.text;
      const result = await translateStoryText(source, 'te');
      setPracticeText(sanitizeStoriesTypingText(result.translatedText));
      setTranslated(true);
      setReadyToType(true);
      toast.success('Translated to English — start typing!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Translation failed. Try again.');
    } finally {
      setTranslating(false);
    }
  };

  const handleSkipTranslation = () => {
    setPracticeText(sanitizeStoriesTypingText(lmsStory?.text || ''));
    setReadyToType(true);
    toast.info('Practicing in original language');
  };

  const handleComplete = async (result) => {
    if (saving || !lesson) return;
    const attemptKey = `${lesson.id}:${result.wpm}:${result.accuracy}:${result.timeSpentSeconds}`;
    if (lastSavedAttemptRef.current === attemptKey) return;
    lastSavedAttemptRef.current = attemptKey;
    setSaving(true);
    try {
      const courseCode = lesson.courseCode || (student?.enrolled_courses || [])[0] || null;
      await saveTypingSession({
        exerciseType,
        lessonId: lesson.id,
        lessonTitle: lesson.title,
        courseCode,
        wpm: result.wpm,
        accuracy: result.accuracy,
        timeSpentSeconds: result.timeSpentSeconds,
        errors: result.errors,
        totalChars: result.totalChars,
        correctChars: result.correctChars,
      });
      setSaved(true);
      toast.success(`Saved: ${result.wpm} WPM — synced to My Journey`);
    } catch (err) {
      lastSavedAttemptRef.current = '';
      toast.error(err.response?.data?.message || 'Could not save session. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = () => {
    lastSavedAttemptRef.current = '';
    setSaved(false);
  };

  if (loadingStory) {
    return (
      <Box className="tt-root">
        <Container sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress color="success" />
          <Typography className="tt-sans" sx={{ mt: 2 }}>Loading practice lesson…</Typography>
        </Container>
      </Box>
    );
  }

  if (!exercise || !lesson) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Lesson not found.</Alert>
        <Button onClick={() => navigate('/student/typing-tutor')} sx={{ mt: 2 }}>
          Back to Typing Tutor
        </Button>
      </Container>
    );
  }

  const typingLesson = {
    ...lesson,
    text: practiceText || lesson.text,
    id: translated ? `${lesson.id}-en` : lesson.id,
  };

  return (
    <Box className="tt-root" sx={exerciseType === 'fingering' ? { bgcolor: '#0f172a', minHeight: '100vh' } : undefined}>
      {exerciseType !== 'fingering' && (
        <Box className="tt-header">
          <Container maxWidth="md">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => navigate(`/student/typing-tutor/${exerciseType}`)}
                sx={{ color: '#fff' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Icon sx={{ color: '#fff' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {lesson.title}
                </Typography>
                <Typography variant="caption" className="tt-sans" sx={{ opacity: 0.9, display: 'block' }}>
                  {lesson.courseName && lesson.chapterTitle
                    ? `${lesson.courseName} · ${lesson.chapterTitle} — `
                    : ''}
                  {exercise.title} — Goal {targetWpm} WPM
                </Typography>
                {(String(lesson.contentType || '').toLowerCase() === 'flashcards'
                  || String(lesson.contentType || '').toLowerCase() === 'flashcard') && (
                  <Chip
                    label="Flash cards"
                    size="small"
                    sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}
                  />
                )}
              </Box>
              {saving && <CircularProgress size={22} sx={{ color: '#fff' }} />}
            </Box>
          </Container>
        </Box>
      )}

      <Container
        maxWidth={exerciseType === 'fingering' ? false : 'md'}
        disableGutters={exerciseType === 'fingering'}
        sx={{ py: exerciseType === 'fingering' ? 0 : 3, px: exerciseType === 'fingering' ? { xs: 0.5, sm: 1 } : undefined }}
      >
        {needsTranslation && !readyToType && (
          <Box className="tt-card" sx={{ p: 2.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TranslateIcon sx={{ color: 'var(--tt-accent)' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Telugu content detected
              </Typography>
              <Chip label="Telugu" size="small" />
            </Box>
            <Typography variant="body2" className="tt-sans" sx={{ color: 'var(--tt-muted)', mb: 2 }}>
              This lesson is in Telugu. Translate to English first for typing practice, or practice in the original language.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={translating ? <CircularProgress size={18} color="inherit" /> : <TranslateIcon />}
                disabled={translating}
                onClick={handleTranslate}
                sx={{ bgcolor: 'var(--tt-accent)' }}
              >
                Translate to English
              </Button>
              <Button variant="outlined" onClick={handleSkipTranslation}>
                Practice in Telugu
              </Button>
            </Box>
          </Box>
        )}

        {readyToType && translated && (
          <Alert severity="info" className="tt-sans" sx={{ mb: 2 }}>
            Translated to English for typing practice
          </Alert>
        )}

        {exerciseType === 'fingering' ? (
          <FingeringEngine
            lesson={typingLesson}
            studentName={student?.name || 'Student'}
            targetWpm={targetWpm}
            onComplete={handleComplete}
            onRetry={handleRetry}
            onCancel={() => navigate(`/student/typing-tutor/${exerciseType}`)}
          />
        ) : readyToType ? (
          <TypingEngine
            lesson={typingLesson}
            targetWpm={targetWpm}
            onComplete={handleComplete}
            onRetry={handleRetry}
            onCancel={() => navigate(`/student/typing-tutor/${exerciseType}`)}
          />
        ) : null}

        {saved && (
          <Alert severity="success" className="tt-sans" sx={{ mt: 2 }}>
            Session saved. Open the KNLS Student app → My Journey to see updated TT records.
          </Alert>
        )}
      </Container>
    </Box>
  );
}
